// User-facing settings: keybindings and camera options. Distinct from config.ts, which
// holds fixed game-tuning constants. These are editable in the Esc menu and persisted to
// localStorage so they survive reloads.

import { CAMERA } from './config';

export type BindableAction =
  | 'forward'
  | 'back'
  | 'left'
  | 'right'
  | 'sprint'
  | 'crouch'
  | 'jump'
  | 'leanLeft'
  | 'leanRight'
  | 'interact'
  | 'inventory'
  | 'crafting'
  | 'build'
  | 'buildHeight'
  | 'buildAlt'
  | 'hotbar1'
  | 'hotbar2'
  | 'hotbar3'
  | 'hotbar4'
  | 'hotbar5'
  | 'hotbar6'
  | 'debug';

export type SprintMode = 'hold' | 'toggle' | 'auto';

export interface Settings {
  binds: Record<BindableAction, string[]>; // KeyboardEvent.code values, multiple allowed per action
  sprintMode: SprintMode; // how the Sprint bind behaves: hold to run / tap to toggle / always run when moving.
  // toggle+auto exist mainly so you never have to HOLD Shift -- Brave/Firefox force an un-suppressable native
  // context menu on Shift+right-click, so holding Shift while aiming (right mouse) pops the browser menu.
  invertMmbY: boolean; // invert vertical look while orbiting with middle mouse
  invertAltY: boolean; // invert vertical look while Alt free-looking
  lookSensitivity: number; // multiplier on look speed
  adsSensitivity: number; // extra multiplier applied ON TOP while aiming down sights (1 = same as hip)
  spatialCompression: number; // camera perspective depth (dolly-zoom fov, degrees)
  fpFov: number; // first-person field of view (degrees)
  volume: number; // master audio volume 0..1
  headbob: boolean; // first-person walk/sprint camera bob (menu toggle; F2 has a matching live knob)
  buildAnim: boolean; // ~0.5s rise-in animation on placed building pieces
}

export const ACTION_LABELS: Record<BindableAction, string> = {
  forward: 'Move forward',
  back: 'Move back',
  left: 'Move left',
  right: 'Move right',
  sprint: 'Sprint',
  crouch: 'Crouch',
  jump: 'Jump',
  leanLeft: 'Peek left',
  leanRight: 'Peek right',
  interact: 'Interact / loot',
  inventory: 'Inventory',
  crafting: 'Crafting',
  build: 'Build mode',
  buildHeight: 'Build rotate / cycle socket',
  buildAlt: 'Build alt (flip / raise)',
  hotbar1: 'Hotbar slot 1',
  hotbar2: 'Hotbar slot 2',
  hotbar3: 'Hotbar slot 3',
  hotbar4: 'Hotbar slot 4',
  hotbar5: 'Hotbar slot 5',
  hotbar6: 'Hotbar slot 6',
  debug: 'Debug overlay',
};

// Hotbar bind actions, in order (used to route a bound key to the matching slot 1..6).
export const HOTBAR_ACTIONS: BindableAction[] = ['hotbar1', 'hotbar2', 'hotbar3', 'hotbar4', 'hotbar5', 'hotbar6'];

export const SENS_MIN = 0.3;
export const SENS_MAX = 2.5;

export const DEFAULT_SETTINGS: Settings = {
  binds: {
    // Movement: WASD + arrow keys by default (the old TFGH alternates are gone -- rebind manually if wanted).
    forward: ['KeyW', 'ArrowUp'],
    back: ['KeyS', 'ArrowDown'],
    left: ['KeyA', 'ArrowLeft'],
    right: ['KeyD', 'ArrowRight'],
    sprint: ['ShiftLeft', 'ShiftRight'],
    crouch: ['KeyC', 'ControlLeft'], // C primary so Ctrl is not the default crouch (Ctrl+W can close the tab)
    jump: ['Space'],
    leanLeft: [], // peek/lean removed - Q/E freed up
    leanRight: [],
    interact: ['KeyE'], // E = interact / loot
    inventory: ['Tab', 'KeyI'], // both open the merged loadout (character + inventory)
    crafting: ['KeyQ'], // Q = the dedicated crafting menu (categories + timed queue)
    build: ['KeyB'],
    buildHeight: ['KeyR'],
    buildAlt: ['KeyF'], // F = the piece's alt action while building (flip door swing / raise foundation)
    hotbar1: ['Digit1'],
    hotbar2: ['Digit2'],
    hotbar3: ['Digit3'],
    hotbar4: ['Digit4'],
    hotbar5: ['Digit5'],
    hotbar6: ['Digit6'],
    debug: ['Backquote'],
  },
  // Default TOGGLE so a fresh player never has to HOLD Shift (which triggers Brave/Firefox's native
  // context menu on Shift+right-click). Tap Shift to run, tap again to walk. Switch to 'hold' in the Esc menu.
  sprintMode: 'toggle',
  invertMmbY: true,
  invertAltY: true,
  lookSensitivity: 1,
  adsSensitivity: 0.8, // slightly slower while sighted feels steadier; 1 = same as hip
  spatialCompression: CAMERA.fovDeg,
  fpFov: CAMERA.fpFovDefault,
  volume: 0.75, // live default 75% (localhost forces 0 at boot); see main.ts
  headbob: true,
  buildAnim: true,
};

// v7: WASD+arrows movement defaults (dropped TFGH), rebindable hotbar 1-6, 75% default volume.
const STORAGE_KEY = 'oxide.settings.v7';

export function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return clone(DEFAULT_SETTINGS);
    const parsed = JSON.parse(raw) as Partial<Settings>;
    // Merge against defaults so actions added in later versions still get a binding.
    const binds = clone(DEFAULT_SETTINGS).binds;
    if (parsed.binds) {
      for (const action of Object.keys(binds) as BindableAction[]) {
        const saved = parsed.binds[action];
        if (Array.isArray(saved) && saved.length) binds[action] = saved.slice();
      }
    }
    return {
      binds,
      sprintMode: parsed.sprintMode === 'hold' || parsed.sprintMode === 'auto' ? parsed.sprintMode : 'toggle',
      invertMmbY: !!parsed.invertMmbY,
      invertAltY: !!parsed.invertAltY,
      lookSensitivity: clampSensitivity(parsed.lookSensitivity),
      adsSensitivity: typeof parsed.adsSensitivity === 'number' ? Math.max(0.2, Math.min(2, parsed.adsSensitivity)) : 0.8,
      spatialCompression: clampFov(parsed.spatialCompression),
      fpFov: clampFpFov(parsed.fpFov),
      volume: typeof parsed.volume === 'number' ? Math.max(0, Math.min(1, parsed.volume)) : 0.75,
      headbob: typeof parsed.headbob === 'boolean' ? parsed.headbob : true, // default ON for saves predating this setting
      buildAnim: typeof parsed.buildAnim === 'boolean' ? parsed.buildAnim : true,
    };
  } catch {
    return clone(DEFAULT_SETTINGS);
  }
}

export function saveSettings(s: Settings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    // private mode or quota; settings just will not persist
  }
}

export function resetSettings(s: Settings): void {
  const d = clone(DEFAULT_SETTINGS);
  s.binds = d.binds;
  s.sprintMode = d.sprintMode;
  s.invertMmbY = d.invertMmbY;
  s.invertAltY = d.invertAltY;
  s.lookSensitivity = d.lookSensitivity;
  s.adsSensitivity = d.adsSensitivity;
  s.spatialCompression = d.spatialCompression;
  s.fpFov = d.fpFov;
  s.volume = d.volume;
  s.headbob = d.headbob;
  saveSettings(s);
}

// Human-readable label for a KeyboardEvent.code.
export function keyLabel(code: string): string {
  if (code.startsWith('Mouse')) {
    const n = Number(code.slice(5));
    return ({ 0: 'L-Mouse', 1: 'M-Mouse', 2: 'R-Mouse', 3: 'Mouse 4 (back)', 4: 'Mouse 5 (fwd)' } as Record<number, string>)[n] ?? ('Mouse ' + n);
  }
  if (code.startsWith('Key')) return code.slice(3);
  if (code.startsWith('Digit')) return code.slice(5);
  if (code.startsWith('Arrow')) return code.slice(5) + ' arrow';
  const named: Record<string, string> = {
    Space: 'Space',
    ShiftLeft: 'L-Shift',
    ShiftRight: 'R-Shift',
    ControlLeft: 'L-Ctrl',
    ControlRight: 'R-Ctrl',
    AltLeft: 'L-Alt',
    AltRight: 'R-Alt',
    Backquote: '`',
    Tab: 'Tab',
    Enter: 'Enter',
  };
  return named[code] ?? code;
}

function clampSensitivity(v: unknown): number {
  const n = typeof v === 'number' && isFinite(v) ? v : 1;
  return Math.min(SENS_MAX, Math.max(SENS_MIN, n));
}

function clampFov(v: unknown): number {
  const n = typeof v === 'number' && isFinite(v) ? v : CAMERA.fovDeg;
  return Math.min(CAMERA.fovMax, Math.max(CAMERA.fovMin, n));
}

function clampFpFov(v: unknown): number {
  const n = typeof v === 'number' && isFinite(v) ? v : CAMERA.fpFovDefault;
  return Math.min(CAMERA.fpFovMax, Math.max(CAMERA.fpFovMin, n));
}

function clone(s: Settings): Settings {
  return JSON.parse(JSON.stringify(s)) as Settings;
}
